import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { retryUploadOnConnectionLoss } from '~/lib/query-client';
import { sendUpload } from '~/lib/upload-attempt';

/**
 * FRONT-1460: the company knowledge bases behind the Knowledge section's
 * "Knowledge bases" tab (`GET /api/knowledge/bases`, backed by the platform
 * knowledge-service v2).
 *
 * Two states that are NOT errors and must render as ordinary copy:
 *  - the `eddy.knowledge-service` flag is off → the route 404s, so gate the
 *    query's `enabled` on the flag rather than showing a failed screen;
 *  - the tenant's own platform KB_V2 flag is off, or nothing is shared with
 *    this user → 200 with an empty list and a `note` explaining why.
 */

export interface KnowledgeBaseItem {
  id: string;
  name: string;
  description: string | null;
  sharedWithYou: boolean;
  canEdit: boolean;
  updatedAt: string | null;
}

export interface KnowledgeBasesResponse {
  items: KnowledgeBaseItem[];
  total: number;
  /** Set when the list is empty for a reason worth telling the user. */
  note?: string;
}

export function useKnowledgeBases(enabled: boolean) {
  return useQuery({
    queryKey: ['knowledge', 'bases'] as const,
    queryFn: () => api.get<KnowledgeBasesResponse>('/api/knowledge/bases'),
    enabled,
    // The corpus changes when someone ingests documents, not per interaction —
    // no need to refetch this on every window focus.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * FRONT-1493: the documents inside one knowledge base.
 *
 * `status` is the field that earns its place: a corpus is routinely part
 * ingested, so a list that shows every document as equal quietly implies the
 * whole thing is searchable when some of it isn't yet — or never will be.
 */
export interface KnowledgeDocumentItem {
  id: string;
  name: string;
  path: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  /** 'pending' | 'processing' | 'ready' | 'failed' | 'partial' — service-defined. */
  status: string | null;
  error: string | null;
  ingestedAt: string | null;
}

export interface KnowledgeDocumentsResponse {
  items: KnowledgeDocumentItem[];
  total: number;
  note?: string;
}

export function useKnowledgeDocuments(knowledgeBaseId: string | null) {
  return useQuery({
    queryKey: ['knowledge', 'documents', knowledgeBaseId] as const,
    queryFn: () =>
      api.get<KnowledgeDocumentsResponse>(
        `/api/knowledge/bases/${encodeURIComponent(knowledgeBaseId as string)}/documents`,
      ),
    enabled: !!knowledgeBaseId,
    // Shorter than the bases list: someone who just uploaded is watching this
    // panel to see the document turn ready.
    staleTime: 30 * 1000,
    // FRONT-2145: without this the list is fetched once and a fresh upload
    // sits on "Processing" until a manual reload. Poll only while something
    // is still in flight; the interval switches itself off once all settle.
    refetchInterval: (query) =>
      hasDocumentsInFlight(query.state.data) ? DOCUMENT_POLL_MS : false,
  });
}

export const DOCUMENT_POLL_MS = 4000;

const SETTLED_STATUSES = new Set(['ready', 'failed', 'partial']);

/**
 * FRONT-2145: is any document still being ingested? An unknown status counts
 * as in flight — the badge renders it as "Processing", so the list must keep
 * checking for as long as it tells the reader to wait. A null status renders
 * no badge and promises nothing, so it does not keep polling alive.
 */
export function hasDocumentsInFlight(data: KnowledgeDocumentsResponse | undefined): boolean {
  return (data?.items ?? []).some(
    (doc) => doc.status !== null && !SETTLED_STATUSES.has(doc.status.toLowerCase()),
  );
}

/**
 * FRONT-1495: add a document to a knowledge base.
 *
 * The server relays the file to the knowledge-service, which stores it and
 * queues ingestion in one call — so a successful response means "accepted",
 * not "searchable". The document list is invalidated on success so the new row
 * appears immediately as Processing; that distinction is what stops someone
 * searching straight away and concluding the file never arrived.
 */
export function useUploadKnowledgeDocument(knowledgeBaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return sendUpload<{ queued: boolean }>(
        `/api/knowledge/bases/${encodeURIComponent(knowledgeBaseId)}/upload`,
        form,
        file.size,
      );
    },
    // Resent only when the connection dropped, never after the server
    // answered (see the policy's docblock).
    retry: retryUploadOnConnectionLoss,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'documents', knowledgeBaseId] });
    },
  });
}

/**
 * FRONT-1497: create a knowledge base. Invalidates the list so the new one
 * appears without a reload.
 */
export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api.post<{ id: string; name: string; description: string | null; canEdit: boolean }>(
        '/api/knowledge/bases',
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'bases'] });
    },
  });
}

/**
 * FRONT-1509: rename a knowledge base or change its description.
 *
 * Invalidates the list so the card and the detail header agree immediately —
 * a rename that only lands in one of the two reads as a bug.
 */
export function useUpdateKnowledgeBase(knowledgeBaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { name?: string; description?: string }) =>
      api.patch<{ id: string; name: string; description: string | null }>(
        `/api/knowledge/bases/${encodeURIComponent(knowledgeBaseId)}`,
        patch,
      ),
    // FRONT-2146: write the SAVED values into the list cache straight from the
    // response, then refetch. The open knowledge base reads its baseline from
    // this cache, so the unsaved-changes bar clears the moment the save lands
    // rather than whenever (or whether) the refetch comes back.
    onSuccess: (saved) => {
      queryClient.setQueryData<KnowledgeBasesResponse>(['knowledge', 'bases'], (old) =>
        old
          ? {
              ...old,
              items: old.items.map((kb) =>
                kb.id === saved.id ? { ...kb, name: saved.name, description: saved.description } : kb,
              ),
            }
          : old,
      );
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'bases'] });
    },
  });
}

/**
 * FRONT-1515: how much of a knowledge base is actually searchable.
 *
 * Counted upstream rather than derived from the document list, which is capped
 * — a large corpus would silently under-report if we counted rows.
 */
export interface KnowledgeSummary {
  total: number;
  ready: number;
  processing: number;
  failed: number;
}

export function useKnowledgeSummary(knowledgeBaseId: string | null) {
  return useQuery({
    queryKey: ['knowledge', 'summary', knowledgeBaseId] as const,
    queryFn: () =>
      api.get<KnowledgeSummary>(
        `/api/knowledge/bases/${encodeURIComponent(knowledgeBaseId as string)}/summary`,
      ),
    enabled: !!knowledgeBaseId,
    staleTime: 30 * 1000,
  });
}
